import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Cloud, Shield, Webhook, Mail, Sparkles, Mic } from 'lucide-react';
import MermaidDiagram from './MermaidDiagram';
import { EDGE_FUNCTIONS_OVERVIEW_CHART } from './edgeFunctionsGuideCharts';

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{title}</h2>
      <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
        {children}
      </div>
    </section>
  );
}

type EdgeFnRow = {
  name: string;
  auth: string;
  caller: string;
  purpose: string;
};

function FnTable({ rows }: { rows: EdgeFnRow[] }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 not-prose">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-100 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-500">
            <th className="px-3 py-2">Function</th>
            <th className="px-3 py-2">Auth</th>
            <th className="px-3 py-2">Called by</th>
            <th className="px-3 py-2">Purpose</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
          {rows.map((row) => (
            <tr key={row.name} className="align-top">
              <td className="px-3 py-2 font-mono text-xs text-orange-700 dark:text-orange-300 whitespace-nowrap">
                {row.name}
              </td>
              <td className="px-3 py-2 whitespace-nowrap text-xs">{row.auth}</td>
              <td className="px-3 py-2">{row.caller}</td>
              <td className="px-3 py-2">{row.purpose}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const toc = [
  { id: 'overview', label: 'Overview' },
  { id: 'auth', label: 'Auth patterns' },
  { id: 'email', label: 'Email & newsletters' },
  { id: 'inngest', label: 'Inngest bridge' },
  { id: 'webhooks', label: 'Inbound webhooks' },
  { id: 'ai', label: 'AI & search' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'related', label: 'Related guides' },
];

const emailFns: EdgeFnRow[] = [
  {
    name: 'materialize-newsletter',
    auth: 'Supabase JWT (gateway)',
    caller: 'Admin — Schedule send / Send now confirm',
    purpose:
      'Renders newsletter blocks to HTML, runs materialize_newsletter_sends RPC (one pending row per recipient). Fires Inngest personalize.requested when rows are inserted.',
  },
  {
    name: 'request-send-scheduled-email',
    auth: 'Supabase JWT',
    caller: 'Admin — Send now on a single scheduled email',
    purpose:
      'Sets scheduled_at to now and queues Inngest scheduled-emails/send.requested. Blocks if AI personalization is still pending.',
  },
  {
    name: 'preview-newsletter',
    auth: 'Supabase JWT',
    caller: 'Admin — newsletter preview panel',
    purpose:
      'Renders draft or saved newsletter HTML without sending. Uses Postmark template validate API when a template alias is set.',
  },
  {
    name: 'preview-email-personalization',
    auth: 'Supabase JWT',
    caller: 'Admin — personalization preview modal',
    purpose:
      'Dry-run AI subject/body tuning for one subscriber; does not write to scheduled_emails.',
  },
  {
    name: 'postmark-templates',
    auth: 'Supabase JWT',
    caller: 'Admin — Postmark templates screen',
    purpose: 'Proxy CRUD for Postmark templates (list, get, create, update, delete) using POSTMARK_API_KEY.',
  },
  {
    name: 'llc-send-mcp-invite',
    auth: 'Bearer JWT in function',
    caller: 'Admin — My AI CTO waitlist',
    purpose:
      'Marks a waitlisted subscriber invited and sends the get-started email to members.stephansmith.me/setup. Does not activate.',
  },
  {
    name: 'llc-send-usage-note',
    auth: 'Bearer JWT in function',
    caller: 'Admin — My AI CTO member detail',
    purpose:
      'Optional note after adjusting monthly call limits: credit more calls, or out-of-calls with upgrade CTA.',
  },
  {
    name: 'postmark-webhook',
    auth: 'Secret in URL path',
    caller: 'Postmark (Open / Click webhooks)',
    purpose:
      'Inserts email_engagement_events. Matches MessageID to scheduled_emails.postmark_message_id.',
  },
];

const webhookFns: EdgeFnRow[] = [
  {
    name: 'subscriber-custom-fields-ingest',
    auth: 'Secret in URL or X-Webhook-Secret',
    caller: 'Zapier, forms, external APIs',
    purpose:
      'Upserts subscriber + custom fields by email. Optional automation_alias to enroll in a flow.',
  },
  {
    name: 'calendar-onboarding',
    auth: 'Secret in URL or X-Webhook-Secret (CALENDAR_ONBOARDING_SECRET)',
    caller: 'BookTime (optional), external webhooks',
    purpose:
      'Enroll subscriber in automation by alias (default calendar_entry_point). Skips if already enrolled once.',
  },
];

const aiFns: EdgeFnRow[] = [
  {
    name: 'transcript-prompt',
    auth: 'Bearer JWT in function',
    caller: 'Admin — transcript Q&A',
    purpose:
      'RAG chat over one transcript: embeds the prompt, retrieves chunks, calls OpenAI. Supports multi-turn history.',
  },
  {
    name: 'index-transcript-chunks',
    auth: 'Bearer JWT in function',
    caller: 'Admin — after transcript save / re-index',
    purpose: 'Chunks transcript text, embeds, upserts transcript_chunks for semantic search.',
  },
  {
    name: 'index-site-content-chunks',
    auth: 'Service role or ORBIT_INDEX_SECRET',
    caller: 'Content CI — export-content-bodies --post',
    purpose:
      'Incremental site article body embed into site_content / site_content_chunks (skips unchanged body_hash).',
  },
  {
    name: 'site-content-prompt',
    auth: 'Bearer JWT in function',
    caller: 'Admin — Writing chat (/writing-chat)',
    purpose:
      'Multi-corpus RAG chat: articles + transcripts + people + saved references. Returns structured refs (paths) for UI chips. Multi-turn history.',
  },
  {
    name: 'index-content-reference-chunks',
    auth: 'Bearer JWT or service role',
    caller: 'Admin — after saving a LI Post / forwarded email reference · batch all=true',
    purpose:
      'Chunk + embed content_references into content_reference_chunks (skips unchanged body_hash).',
  },
  {
    name: 'extract-orbit-followup',
    auth: 'Bearer JWT or service role',
    caller: 'process-transcript-receipt (ORBIT_FOLLOWUP_ENABLED) · Transcript detail · backfill',
    purpose:
      'Tear apart transcript signals, match site_content_chunks, insert held_emails (orbit_followup). No send.',
  },
  {
    name: 'process-transcript-receipt',
    auth: 'Bearer JWT or service role',
    caller: 'readai-webhook post-insert · Transcript detail Run receipt',
    purpose:
      'Replays receipt steps: index chunks, review task, task extraction, orbit follow-up when enabled, and person-chat attach. Reports each step.',
  },
  {
    name: 'index-connection-embeddings',
    auth: 'Bearer JWT in function',
    caller: 'Admin — connection profile indexing',
    purpose:
      'Embeds ICP and call-me-when fields on a connection for semantic subscriber search.',
  },
  {
    name: 'search-connections-semantic',
    auth: 'Bearer JWT in function',
    caller: 'Admin — semantic connection search',
    purpose: 'Natural-language query → embedding → match_connections_semantic RPC.',
  },
  {
    name: 'newsletter-invite-suggest',
    auth: 'Bearer JWT in function',
    caller: 'Admin — newsletter invite helper',
    purpose:
      'Given meeting transcript summaries + site articles, suggests which article link to include in an invite.',
  },
];

const integrationFns: EdgeFnRow[] = [
  {
    name: 'calendly-meetings',
    auth: 'Supabase JWT',
    caller: 'Admin — subscriber Calendly section',
    purpose:
      'Proxies Calendly scheduled events + invitee details (rate-limited, batched) for display in the UI.',
  },
];

export default function EdgeFunctionsGuideView() {
  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800">
            <Cloud className="w-6 h-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Supabase edge functions
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              HTTP endpoints deployed with the engine — what each one does and who may call it.
            </p>
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Related:{' '}
          <Link
            to="/guide/inngest"
            className="text-violet-600 dark:text-violet-400 underline font-medium"
          >
            Inngest background jobs
          </Link>
          {' · '}
          <Link
            to="/guide/email-pipeline"
            className="text-emerald-600 dark:text-emerald-400 underline font-medium"
          >
            Send pipeline
          </Link>
        </p>
      </div>

      <nav className="mb-8 flex flex-wrap gap-2 text-xs">
        {toc.map((item) => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className="px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:border-orange-300 dark:hover:border-orange-700 transition-colors"
          >
            {item.label}
          </a>
        ))}
      </nav>

      <div className="space-y-10">
        <Section id="overview" title="Overview">
          <p>
            Each folder under <code className="text-xs font-mono">supabase/functions/</code> is one
            deployable edge function. The admin app calls most of them with{' '}
            <code className="text-xs font-mono">supabase.functions.invoke('name', …)</code>.
            External systems (Postmark, Zapier) call webhook URLs directly.
          </p>
          <MermaidDiagram
            chart={EDGE_FUNCTIONS_OVERVIEW_CHART}
            caption="JWT at the gateway vs auth checked inside the function vs URL secrets."
          />
          <p>
            Shared code lives in <code className="text-xs font-mono">supabase/functions/_shared/</code>{' '}
            — those modules are not deployed as their own endpoints.
          </p>
        </Section>

        <Section id="auth" title="Auth patterns">
          <div className="grid gap-3 sm:grid-cols-3 not-prose">
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <Shield className="w-5 h-5 text-emerald-500 mb-2" />
              <p className="font-medium text-gray-900 dark:text-white text-sm">Gateway JWT</p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                <code className="font-mono">verify_jwt = true</code> in config.toml. Caller must be a
                signed-in admin; RLS applies when the function uses the user’s JWT with anon key.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <Shield className="w-5 h-5 text-amber-500 mb-2" />
              <p className="font-medium text-gray-900 dark:text-white text-sm">JWT in function</p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Gateway allows unauthenticated OPTIONS; POST must include{' '}
                <code className="font-mono">Authorization: Bearer …</code> validated in code (CORS-friendly).
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <Webhook className="w-5 h-5 text-sky-500 mb-2" />
              <p className="font-medium text-gray-900 dark:text-white text-sm">URL secret</p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Last path segment or header compared to an env secret. Used for vendor webhooks and
                ingest endpoints — no Supabase user session.
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            Invoke URL shape:{' '}
            <code className="font-mono">
              https://&lt;project-ref&gt;.supabase.co/functions/v1/&lt;function-name&gt;
            </code>
          </p>
        </Section>

        <Section id="email" title="Email & newsletters">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
            <Mail className="w-4 h-4 text-emerald-500" />
            Sending, previews, templates, engagement webhooks
          </div>
          <FnTable rows={emailFns} />
        </Section>

        <Section id="inngest" title="Inngest bridge">
          <FnTable
            rows={[
              {
                name: 'inngest',
                auth: 'Inngest signing key',
                caller: 'Inngest Cloud only',
                purpose:
                  'Registers all background workers (serve handler). Not for browser or admin use. Deploy with verify_jwt = false.',
              },
            ]}
          />
          <p>
            See the{' '}
            <Link to="/guide/inngest" className="underline text-violet-600 dark:text-violet-400">
              Inngest guide
            </Link>{' '}
            for cron schedules and event names.
          </p>
        </Section>

        <Section id="webhooks" title="Inbound webhooks">
          <FnTable rows={webhookFns} />
          <p>
            Postmark tracking details:{' '}
            <Link to="/guide/email-tracking" className="underline text-sky-600 dark:text-sky-400">
              Open/click tracking guide
            </Link>
            .
          </p>
        </Section>

        <Section id="ai" title="AI & search">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
            <Sparkles className="w-4 h-4 text-amber-500" />
            OpenAI embeddings and chat
          </div>
          <FnTable rows={aiFns} />
          <p className="text-xs text-gray-500">
            Most require <code className="font-mono">OPENAI_API_KEY</code>. Personalization at send
            time runs in Inngest, not in these HTTP functions.
          </p>
        </Section>

        <Section id="integrations" title="Integrations">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
            <Mic className="w-4 h-4 text-emerald-500" />
            Third-party APIs
          </div>
          <FnTable rows={integrationFns} />
          <p className="text-xs text-gray-500">
            Calendly uses <code className="font-mono">CALENDLY_API_KEY</code>.
          </p>
        </Section>

        <Section id="related" title="Related guides">
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <Link to="/guide/inngest" className="underline text-violet-600 dark:text-violet-400">
                Inngest background jobs
              </Link>{' '}
              — crons and events triggered from edge functions
            </li>
            <li>
              <Link
                to="/guide/email-pipeline"
                className="underline text-emerald-600 dark:text-emerald-400"
              >
                Email send pipeline
              </Link>
            </li>
            <li>
              <Link to="/guide/email-tracking" className="underline text-sky-600 dark:text-sky-400">
                Open/click tracking
              </Link>
            </li>
          </ul>
        </Section>
      </div>
    </div>
  );
}
